import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { denormalisedResponseEntities, uuidToPlainString } from '../../util/data';
import { storableError } from '../../util/errors';
import { fetchCurrentUserThunk, setCurrentUser } from '../../ducks/user.duck';
import { types as sdkTypes } from '../../util/sdkLoader';
import { omitReservedManagedAuthPrivateData } from '../../config/r2rManagedAuthPrivateData';
import { pickUploadedImageUrl } from '../../config/r2rCommercialSeller';

const { UUID } = sdkTypes;

/**
 * Listing flows pass raw SDK UUID instances to `sdk.ownListings.*`. Marketplace Transit
 * expects the same typed UUID objects for profileImageId — plain JS strings often get encoded
 * as generic strings (`:profileImageId, "uuid"`), which the backend may ignore while still
 * returning 200 with an unchanged profile.
 */
const coerceProfileImageUuidForSdk = rawProfileImageId => {
  if (rawProfileImageId == null || rawProfileImageId === '') {
    return undefined;
  }
  if (typeof rawProfileImageId === 'object' && rawProfileImageId._sdkType === 'UUID') {
    return rawProfileImageId;
  }

  const plain = uuidToPlainString(rawProfileImageId);
  return plain ? new UUID(plain) : undefined;
};

// ================ Async Thunks ================ //

//////////////////
// Upload Image //
//////////////////
export const uploadImageThunk = createAsyncThunk(
  'ProfileSettingsPage/uploadImage',
  ({ id, file }, { rejectWithValue, extra: sdk }) => {
    const bodyParams = {
      image: file,
    };
    const queryParams = {
      expand: true,
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.images
      .upload(bodyParams, queryParams)
      .then(resp => {
        const uploadedImage = resp.data.data;
        if (!uploadedImage?.id) {
          throw new Error('Image upload succeeded but returned no image id');
        }
        return { id, uploadedImage };
      })
      .catch(e => rejectWithValue({ id, error: storableError(e) }));
  }
);

export const uploadImage = payload => async dispatch => {
  const uploadAction = await dispatch(uploadImageThunk(payload));

  if (uploadImageThunk.fulfilled.match(uploadAction)) {
    const imageId = uploadAction.payload.uploadedImage.id;
    await dispatch(updateProfileThunk({ profileImageId: imageId }));
  }

  return uploadAction;
};

/////////////////////////
// Upload Banner Image //
/////////////////////////
export const uploadBannerImageThunk = createAsyncThunk(
  'ProfileSettingsPage/uploadBannerImage',
  async ({ file }, { dispatch, rejectWithValue, extra: sdk }) => {
    const bodyParams = { image: file };
    const queryParams = {
      expand: true,
      'fields.image': [
        'variants.scaled-xlarge',
        'variants.scaled-large',
        'variants.scaled-medium',
        'variants.scaled-small',
      ],
    };

    try {
      const resp = await sdk.images.upload(bodyParams, queryParams);
      const uploadedImage = resp.data.data;
      if (!uploadedImage?.id) {
        throw new Error('Banner upload succeeded but returned no image id');
      }
      const profileBannerUrl = pickUploadedImageUrl(uploadedImage);
      if (!profileBannerUrl) {
        throw new Error('Banner upload succeeded but returned no usable image URL');
      }
      const profileBannerImageId = uuidToPlainString(uploadedImage.id);
      await dispatch(
        updateProfileThunk({
          publicData: {
            profileBannerUrl,
            profileBannerImageId,
          },
        })
      ).unwrap();
      return { profileBannerUrl, profileBannerImageId };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const uploadBannerImage = payload => dispatch => dispatch(uploadBannerImageThunk(payload));

export const removeBannerImage = () => dispatch =>
  dispatch(
    updateProfileThunk({
      publicData: {
        profileBannerUrl: null,
        profileBannerImageId: null,
      },
    })
  );

////////////////////
// Update Profile //
////////////////////
export const updateProfileThunk = createAsyncThunk(
  'ProfileSettingsPage/updateProfile',
  async (actionPayload, { dispatch, rejectWithValue, extra: sdk, getState }) => {
    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    try {
      const { profileImageId: rawPid, ...restPayload } = actionPayload || {};
      const coercedPid = coerceProfileImageUuidForSdk(rawPid);
      const sdkBody = {
        ...restPayload,
        ...(restPayload.privateData != null
          ? { privateData: omitReservedManagedAuthPrivateData(restPayload.privateData) }
          : {}),
        ...(coercedPid ? { profileImageId: coercedPid } : {}),
      };

      const response = await sdk.currentUser.updateProfile(sdkBody, queryParams);
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }

      const updatedEntity = entities[0];
      dispatch(setCurrentUser(updatedEntity));

      const profileImageAttached = coercedPid != null;

      if (profileImageAttached) {
        try {
          await dispatch(
            fetchCurrentUserThunk({
              enforce: true,
              updateHasListings: false,
              updateNotifications: false,
            })
          ).unwrap();
        } catch {
          /* Best-effort refresh; profile already updated via setCurrentUser above */
        }
      }

      return response;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);
// Backward compatible wrapper for the updateProfile thunk
export const updateProfile = actionPayload => dispatch => {
  return dispatch(updateProfileThunk(actionPayload));
};

// ================ Slice ================ //

const profileSettingsPageSlice = createSlice({
  name: 'ProfileSettingsPage',
  initialState: {
    image: null,
    uploadImageError: null,
    uploadInProgress: false,
    bannerUploadInProgress: false,
    bannerUploadError: null,
    updateInProgress: false,
    updateProfileError: null,
  },
  reducers: {
    clearUpdatedForm: state => {
      state.updateProfileError = null;
      state.uploadImageError = null;
      state.bannerUploadError = null;
    },
  },
  extraReducers: builder => {
    builder
      // uploadImage cases
      .addCase(uploadImageThunk.pending, (state, action) => {
        const { id, file } = action.meta.arg;
        state.image = { id, file };
        state.uploadInProgress = true;
        state.uploadImageError = null;
      })
      .addCase(uploadImageThunk.fulfilled, (state, action) => {
        const { id, uploadedImage } = action.payload;
        const { file } = state.image || {};
        state.image = { id, imageId: uploadedImage.id, file, uploadedImage };
        state.uploadInProgress = false;
      })
      .addCase(uploadImageThunk.rejected, (state, action) => {
        state.image = null;
        state.uploadInProgress = false;
        state.uploadImageError = action.payload.error;
      })
      .addCase(uploadBannerImageThunk.pending, state => {
        state.bannerUploadInProgress = true;
        state.bannerUploadError = null;
      })
      .addCase(uploadBannerImageThunk.fulfilled, state => {
        state.bannerUploadInProgress = false;
      })
      .addCase(uploadBannerImageThunk.rejected, (state, action) => {
        state.bannerUploadInProgress = false;
        state.bannerUploadError = action.payload;
      })
      // updateProfile cases
      .addCase(updateProfileThunk.pending, state => {
        state.updateInProgress = true;
        state.updateProfileError = null;
      })
      .addCase(updateProfileThunk.fulfilled, state => {
        state.image = null;
        state.updateInProgress = false;
      })
      .addCase(updateProfileThunk.rejected, (state, action) => {
        state.updateInProgress = false;
        state.updateProfileError = action.payload;
      });
  },
});

export const { clearUpdatedForm } = profileSettingsPageSlice.actions;
export default profileSettingsPageSlice.reducer;
